import type {
    IIssueSuggestedQuestionsResponse,
    IssueActivityResponseUnion,
    IssueResponseBase,
} from '@kcftech/prometheus-client';
import { IssueStatusType } from '@kcftech/prometheus-client';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { getEntity, updateEntities } from 'shared/normalized_cache';
import { queryClient } from 'utils/query';
import type { RecurseNonSdkClass } from 'utils/sdk_types';
import { issueQueryOptions } from '../useIssue';
import { getIssueStatusOrPlaceholder } from '../useIssueStatuses';

export type IssueEvent = Readonly<
    | {
          type: 'activity';
          action: 'create' | 'update' | 'delete';
          activityId: string;
          activity: IssueActivityResponseUnion;
      }
    | {
          type: 'activity-remove';
          activityId: string;
          issueId: string;
      }
    | {
          type: 'issue';
          action: 'create' | 'update' | 'view';
          issueId: string;
      }
    | {
          type: 'flag';
          action: 'update';
          issueId: string;
          flagInfo: {
              flagged: boolean;
              shouldReceiveNotifications: boolean;
              isArchived: boolean;
          };
      }
    | {
          type: 'task';
          action: 'create' | 'update' | 'delete';
          workItemId: string;
      }
    | {
          type: 'suggestedQuestions';
          response: IIssueSuggestedQuestionsResponse;
      }
>;

export const shouldCauseIssueRefetch = (event: IssueEvent): boolean => {
    return (
        event.type === 'issue' ||
        (event.type === 'activity' &&
            !(
                event.activity.discriminator === 'Comment' ||
                event.activity.discriminator === 'CommentDelete' ||
                event.activity.discriminator === 'CommentReply' ||
                event.activity.discriminator === 'ChartData'
            )) ||
        event.type === 'flag'
    );
};

/** The issue an event is about, or `undefined` if it isn't about one. */
export function getEventIssueId(event: IssueEvent): string | undefined {
    switch (event.type) {
        case 'activity':
            return event.activity.IssueId;
        case 'activity-remove':
        case 'issue':
        case 'flag':
            return event.issueId;
        case 'task':
        case 'suggestedQuestions':
            return undefined;
    }
}

/** An issue's fields that an event changes. */
export type IssueEventFields = Partial<
    Pick<
        RecurseNonSdkClass<IssueResponseBase>,
        'IssueStatus' | 'Priority' | 'Severity' | 'RecommendedAction' | 'Description'
    >
>;

/**
 * The fields of its issue that `event` changes, or `undefined` if it changes
 * none that are known from the event alone.
 *
 * @param currentStatusType the issue's status type before the event, if known
 */
export function getIssueFieldsForEvent(
    event: IssueEvent,
    currentStatusType: IssueStatusType | undefined,
): IssueEventFields | undefined {
    if (event.type !== 'activity') {
        return undefined;
    }

    switch (event.activity.discriminator) {
        case 'Publish':
            if (currentStatusType !== IssueStatusType.Draft) {
                return undefined;
            }
            return {
                IssueStatus: getIssueStatusOrPlaceholder(IssueStatusType.New, 'New'),
            };
        case 'StatusUpdate': {
            const { ToStatusType, ToStatusName } = event.activity;
            return {
                IssueStatus: getIssueStatusOrPlaceholder(ToStatusType, ToStatusName),
            };
        }
        case 'PriorityUpdate':
            return { Priority: event.activity.ToPriority };
        case 'SeverityUpdate':
            return { Severity: event.activity.ToSeverity };
        case 'RecommendationUpdate':
            return { RecommendedAction: event.activity.ToRecommendation };
        case 'DescriptionUpdate':
            return { Description: event.activity.ToDescription };
        default:
            return undefined;
    }
}

/**
 * Applies `event`'s change to its issue wherever normalized queries hold it,
 * ahead of refetching the issue.
 */
export function applyIssueEvent(event: IssueEvent) {
    const issueId = getEventIssueId(event);
    if (issueId === undefined) {
        return;
    }
    const issue = getEntity('issue', issueId);
    if (issue === undefined) {
        return;
    }
    const fields = getIssueFieldsForEvent(event, issue.IssueStatus?.StatusType);
    if (fields) {
        updateEntities([{ kind: 'issue', id: issueId, fields }]);
    }
}

/**
 * Keeps `event`'s issue current wherever normalized queries hold it: applies
 * the event's change right away where it has one, then refetches the issue.
 * Does nothing for events that don't affect an issue held in the cache.
 */
export function syncIssueForEvent(event: IssueEvent) {
    const issueId = getEventIssueId(event);
    if (
        issueId === undefined ||
        !shouldCauseIssueRefetch(event) ||
        getEntity('issue', issueId) === undefined
    ) {
        return;
    }
    applyIssueEvent(event);
    // failures are reported by the query cache
    queryClient.fetchQuery(issueQueryOptions(issueId)).catch(() => undefined);
}

export type IssueEventCallback = (event: IssueEvent) => void;

export type IssueEventsContext = Readonly<{
    registerListener: (callback: IssueEventCallback) => () => void;
    triggerIssueEvent: (event: IssueEvent) => void;
    destroy: () => void;
}>;

const DedupWindowMs = 10_000;

function getEventDedupeKey(event: IssueEvent): string | undefined {
    switch (event.type) {
        case 'activity':
            return `activity:${event.activityId}:${event.action}`;
        case 'task':
            return `task:${event.workItemId}:${event.action}`;
        default:
            return undefined;
    }
}

function buildIssueEventContext() {
    const callbacks = new Set<IssueEventCallback>();
    const recentEvents = new Set<string>();

    const contextValue: IssueEventsContext = {
        registerListener: (callback) => {
            callbacks.add(callback);

            return () => {
                callbacks.delete(callback);
            };
        },
        triggerIssueEvent: (event) => {
            // in order to avoid the same event potentially being emitted from
            // several sources (e.g. a local update followed by a signalr copy
            // of the same update), dedupe same events we've recently seen
            const key = getEventDedupeKey(event);
            if (key) {
                if (recentEvents.has(key)) {
                    return;
                }
                recentEvents.add(key);
                setTimeout(() => recentEvents.delete(key), DedupWindowMs);
            }
            // exec
            callbacks.forEach((callback) => callback(event));
        },
        destroy: () => {
            callbacks.clear();
            recentEvents.clear();
        },
    };

    return contextValue;
}

const Context = createContext<IssueEventsContext | undefined>(undefined);

export type IssueEventsProviderProps = Readonly<{ children?: ReactNode }>;

export function IssueEventsProvider({ children }: IssueEventsProviderProps) {
    const value: IssueEventsContext = useMemo(() => buildIssueEventContext(), []);

    useEffect(() => () => value.destroy(), [value]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useIssueEventsContext() {
    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('Missing <IssueEventsProvider>');
    }

    return ctx;
}

export function useOnIssueEvent(callback: IssueEventCallback) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const { registerListener } = useIssueEventsContext();

    useEffect(() => {
        const unregister = registerListener((event) => callbackRef.current(event));
        return unregister;
    }, [registerListener]);
}

export function useTriggerIssueEvent() {
    return useIssueEventsContext().triggerIssueEvent;
}
